import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import type { ProjectComparisonDto, ProjectDto } from "../../../models"
import type { AppState, AppThunkDispatch, BosatlasAction, Bundle, Comparison, Project } from "../../../redux"
import { bosatlasFetchBundle, fetchBosatlas } from "../../../redux"
import { BosatlasViewer, BundleViewer } from "../../organisms"
import { BackButton } from "../back-button/back-button.component"

export interface IBosatlasMapSelectorProps {
  disabled?: boolean
  projectSelection: Project[]
  comparisonSelection?: Comparison[]
  sharing?: boolean
  onProjectClicked: (project: Project) => void
  onComparisonClicked?: (comparison: Comparison) => void
  onShowPreview: (project: Project) => void
}

export interface IBosatlasMapSelectorStateProps {
  bundles?: Bundle[]
  projects?: ProjectDto[]
  comparisons?: ProjectComparisonDto[]
  bundle?: Bundle
  fetchBosatlas: () => void
  fetchBundle: (bundleId: string) => void
}

function BosatlasMapSelectorView(props: IBosatlasMapSelectorProps & IBosatlasMapSelectorStateProps): ReactElement {
  const {
    disabled,
    bundle,
    bundles,
    projects,
    sharing,
    comparisons,
    projectSelection,
    comparisonSelection,
    onProjectClicked,
    onComparisonClicked,
    onShowPreview,
    fetchBosatlas,
    fetchBundle
  } = props

  const [showBundleContent, setShowBundleContent] = useState<boolean>(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchBosatlas()
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (!showBundleContent) fetchBosatlas()
  }, [showBundleContent])

  function handleProjectClick(project: ProjectDto): void {
    onProjectClicked(toProject(project))
  }

  function handleComparisonClick(comparison: ProjectComparisonDto): void {
    if (onComparisonClicked) onComparisonClicked(toComparison(comparison))
  }

  function isProjectSelected(project: ProjectDto): boolean {
    return projectSelection.find((p: Project) => p.id === project.id) !== undefined
  }

  function isComparisonSelected(comparison: ProjectComparisonDto): boolean {
    if (!comparisonSelection) return false
    return comparisonSelection.find((c: Comparison) => c.id === comparison.id) !== undefined
  }

  function toProject(dto: ProjectDto): Project {
    return {
      id: dto.id,
      title: dto.title,
      metaDataId: dto.metadataId,
      attachments: [],
      drawings: dto.drawing ? JSON.parse(dto.drawing) : [],
      bundles: [],
      thumbnailUrl: dto.assetThumbnailUrl
    }
  }

  function toComparison(dto: ProjectComparisonDto): Comparison {
    return {
      id: dto.id,
      title: dto.title,
      layout: dto.layout,
      firstAssetThumbnailUrl: dto.firstAssetThumbnailUrl,
      secondAssetThumbnailUrl: dto.secondAssetThumbnailUrl,
      thirdAssetThumbnailUrl: dto.thirdAssetThumbnailUrl,
      fourthAssetThumbnailUrl: dto.fourthAssetThumbnailUrl,
      projects: [],
      bundles: []
    }
  }

  function showBundleViewer(bundleId: string): void {
    fetchBundle(bundleId)
    setShowBundleContent(true)
  }

  function backToMain(): void {
    setShowBundleContent(false)
  }

  function handleProjectPreview(project: ProjectDto): void {
    onShowPreview(toProject(project))
  }

  function mapBundlesToNoComparisons(): Bundle[] | undefined {
    if (!bundles) return undefined
    return bundles.map((bundle: Bundle) => {
      return {
        ...bundle,
        contentAmount: bundle.projectAmount ? bundle.projectAmount : 0
      }
    })
  }

  return (
    <>
      {showBundleContent && bundle && (
        <>
          <BackButton onClick={backToMain} />
          <BundleViewer
            disabled={disabled}
            bundle={bundle}
            projects={bundle.projects ? bundle.projects : []}
            comparisons={[]}
            disableEditing
            selectable
            onProjectPreview={handleProjectPreview}
            isProjectSelected={isProjectSelected}
            onProjectClick={handleProjectClick}
          />
        </>
      )}
      {!showBundleContent && (
        <BosatlasViewer
          disabled={disabled}
          sharing={sharing}
          disableEditing
          selectable
          bundles={mapBundlesToNoComparisons()}
          projects={projects}
          comparisons={onComparisonClicked ? comparisons : []}
          onProjectPreview={handleProjectPreview}
          onProjectClick={handleProjectClick}
          onComparisonClick={handleComparisonClick}
          isProjectSelected={isProjectSelected}
          isComparisonSelected={isComparisonSelected}
          onViewBundleContentClick={showBundleViewer}
        />
      )}
    </>
  )
}

function mapStateToProps(state: AppState) {
  return {
    bundles: state.bosatlas.bundles,
    projects: state.bosatlas.projects,
    bundle: state.bosatlas.bundle,
    comparisons: state.bosatlas.projectComparisons
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasAction>) {
  return {
    fetchBosatlas: () => dispatch(fetchBosatlas()),
    fetchBundle: (bundleId: string) => dispatch(bosatlasFetchBundle(bundleId))
  }
}

export const BosatlasMapSelector = connect(mapStateToProps, mapDispatchToProps)(BosatlasMapSelectorView)
